import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type {
  CreateDeviceCategoryInput,
  DeviceCategoryDto,
  PrinterDto,
  UpdateDeviceCategoryInput,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api } from '@/lib/api';
import { Field, ghostPillClass, inputClass, pillClass } from './controls';
import { printerChoiceLabel } from './printerTest';
import { toFieldErrors, useResource, type FieldError } from './useSettings';

/**
 * Device categories (SPEC §6.2) — the grouping every device belongs to, and the
 * printer its tickets route to.
 *
 * This screen did not exist, and its absence was load-bearing: Add Device
 * REQUIRES a category, so a venue starting from an empty database could not add
 * a single device. Only the demo seed's direct database writes hid it.
 */
export function DeviceCategoriesPage() {
  const { t } = useTranslation();
  const { can } = useAuth();

  const { data, loading, reload } = useResource<DeviceCategoryDto[]>('/device-categories');
  // Loaded here rather than in the dialog so the table can say which printers
  // are switched off. Printers are `printers.manage`; without it the table
  // shows names only and the picker offers the venue default, as before.
  const printers = useResource<PrinterDto[]>(can('printers.manage') ? '/settings/printers' : null);
  const printerList = printers.data ?? [];
  const [editing, setEditing] = useState<DeviceCategoryDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = data ?? [];

  async function handleDelete(category: DeviceCategoryDto): Promise<void> {
    if (!window.confirm(t('settings.deviceCategories.confirmDelete', { name: category.name })))
      return;
    try {
      await api.delete(`/device-categories/${category.id}`);
      await reload();
    } catch (caught) {
      // The server refuses a category that still has devices and says how many;
      // that message is more useful than anything this screen could invent.
      setError(
        toFieldErrors(caught, t('settings.deviceCategories.deleteFailed'))[0]?.message ?? null,
      );
    }
  }

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content">{t('settings.nav.deviceCategories')}</h1>
        <div className="flex-1" />
        <button type="button" onClick={() => setCreating(true)} className={pillClass}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.deviceCategories.addNew')}
          </span>
        </button>
      </header>

      {/* A brand-new venue lands here with nothing, having been sent from Add
          Device. Say what this is for rather than showing an empty table. */}
      {!loading && categories.length === 0 ? (
        <p className="mb-4 rounded-card border border-subtle bg-raised p-4 text-sm text-content-secondary">
          {t('settings.deviceCategories.emptyHint')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-card border border-subtle">
        <table className="w-full min-w-[40rem] max-md:min-w-0 border-collapse text-sm">
          <thead>
            <tr className="bg-raised text-content-secondary">
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('common.name')}
              </th>
              {/* Printer routing is a desktop admin concern — its long names
                  were the 53px this table swiped by on a phone. */}
              <th className="border-b border-subtle p-3 text-start font-medium max-md:hidden">
                {t('settings.deviceCategories.printer')}
              </th>
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('settings.deviceCategories.devices')}
              </th>
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('common.edit')}
              </th>
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('common.delete')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && categories.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-content-muted">
                  {t('common.loading')}
                </td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-content-muted">
                  {t('common.noResults')}
                </td>
              </tr>
            ) : (
              categories.map((category, index) => (
                <tr
                  key={category.id}
                  className={index % 2 === 0 ? 'bg-[var(--row-even)]' : 'bg-[var(--row-odd)]'}
                >
                  <td className="border-b border-subtle p-3 text-content">{category.name}</td>
                  <td className="border-b border-subtle p-3 text-content-secondary max-md:hidden">
                    {category.printerName
                      ? printerChoiceLabel(
                          {
                            name: category.printerName,
                            isActive:
                              printerList.find((p) => p.id === category.printerId)?.isActive ??
                              true,
                          },
                          t('settings.general.printerOff'),
                        )
                      : t('settings.deviceCategories.defaultPrinter')}
                  </td>
                  <td className="border-b border-subtle p-3 tabular text-content-secondary">
                    {category.deviceCount}
                  </td>
                  <td className="border-b border-subtle p-3">
                    <button
                      type="button"
                      onClick={() => setEditing(category)}
                      title={t('common.edit')}
                      aria-label={t('common.edit')}
                      className="grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover"
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                  <td className="border-b border-subtle p-3">
                    <button
                      type="button"
                      onClick={() => void handleDelete(category)}
                      title={t('common.delete')}
                      aria-label={t('common.delete')}
                      className="grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {creating ? (
        <DeviceCategoryDialog
          category={null}
          printers={printerList}
          onClose={() => setCreating(false)}
          onSaved={() => void reload()}
        />
      ) : null}

      {editing ? (
        <DeviceCategoryDialog
          category={editing}
          printers={printerList}
          onClose={() => setEditing(null)}
          onSaved={() => void reload()}
        />
      ) : null}
    </div>
  );
}

function DeviceCategoryDialog({
  category,
  printers,
  onClose,
  onSaved,
}: {
  category: DeviceCategoryDto | null;
  printers: readonly PrinterDto[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = category !== null;

  const [name, setName] = useState(category?.name ?? '');
  const [printerId, setPrinterId] = useState(category?.printerId ?? '');
  const [sortOrder, setSortOrder] = useState(String(category?.sortOrder ?? 0));

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);

    // '' is what the "venue default printer" option sends; the contract turns it
    // into null rather than a foreign key that matches nothing.
    const common = {
      name,
      printerId: printerId || null,
      sortOrder: Number(sortOrder) || 0,
    };

    try {
      if (isEdit && category) {
        await api.patch(
          `/device-categories/${category.id}`,
          common satisfies UpdateDeviceCategoryInput,
        );
      } else {
        await api.post('/device-categories', common satisfies CreateDeviceCategoryInput);
      }
      onSaved();
      onClose();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('settings.deviceCategories.saveFailed')));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="max-h-full w-full max-w-lg overflow-auto rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-content">
          {isEdit ? t('settings.deviceCategories.edit') : t('settings.deviceCategories.addNew')}
        </h2>

        <div className="space-y-3">
          <Field
            label={t('common.name')}
            hint={t('settings.deviceCategories.nameHint')}
            error={errorFor('name')}
          >
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={60}
              className={inputClass}
            />
          </Field>

          <Field
            label={t('settings.deviceCategories.printer')}
            hint={t('settings.deviceCategories.printerHint')}
            error={errorFor('printerId')}
          >
            <select
              value={printerId}
              onChange={(event) => setPrinterId(event.target.value)}
              className={inputClass}
            >
              <option value="">{t('settings.deviceCategories.defaultPrinter')}</option>
              {printers.map((printer) => (
                <option key={printer.id} value={printer.id}>
                  {printerChoiceLabel(printer, t('settings.general.printerOff'))}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('settings.deviceCategories.sortOrder')} error={errorFor('sortOrder')}>
            <input
              type="number"
              min={0}
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
              className={`${inputClass} tabular`}
            />
          </Field>
        </div>

        <footer className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={ghostPillClass}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={submitting} className={pillClass}>
            {t('common.save')}
          </button>
        </footer>
      </form>
    </div>
  );
}
